import type { IFilters, ISearchResult } from "@features/Pages/SearchPage/Scripts/search.types";

import {
  pageId,
  currentLanguage,
  isListingPage
} from "@features/Pages/SearchPage/Scripts/search.setup";

export interface ISearchRequestState {
  query: string | null;
  apiSkipAmount: number;
  apiTakeAmount: number;
  selectedContentTypes: string[];
  selectedSubjects: string[];
  selectedEventStatus: string | null;
  selectedSortOrder: "asc" | "desc";
  apiAncestors: string | null;
}

let searchIsLoading = false;
export const isSearchLoading = () => searchIsLoading;

export const getSearchResult = async (
  state: ISearchRequestState,
  toggleLoadingSpinner: (isLoadMore: boolean) => void,
  takeAll: boolean = false,
  isLoadMore: boolean = false
): Promise<ISearchResult | void> => {
  const {
    query,
    apiSkipAmount,
    apiTakeAmount,
    selectedContentTypes,
    selectedSubjects,
    selectedEventStatus,
    selectedSortOrder,
    apiAncestors
  } = state;
  /**
   * In case the user entered a URL or navigated back to the page,
   * the take and skip amounts can be incorrect.
   * ex. if items are skipped, we need to take all of the skipped items.
   * without changing the varialbes for apiTakeAmount and apiSkipAmount.
   */

  let takeAmount = apiTakeAmount;
  let skipAmount = apiSkipAmount;

  if (takeAll) {
    const urlParams = new URLSearchParams(window.location.search) as URLSearchParams;
    const takeFromParam: string | null = urlParams.get('take');
    const skipFromParam: string | null = urlParams.get('skip');
    let takeFromParamNum = 0;
    let skipFromParamNum = 0;

    if (skipFromParam && !isNaN(parseInt(skipFromParam, 10))) {
      skipFromParamNum = parseInt(skipFromParam, 10);
    }

    if (takeFromParam && !isNaN(parseInt(takeFromParam, 10))) {
      takeFromParamNum = parseInt(takeFromParam, 10);
    }

    if (skipFromParamNum > 0) {
      takeAmount = skipFromParamNum + takeFromParamNum;
      skipAmount = 0;
    }
  }

  let path = `/api/search?pageid=${pageId}&query=${query}&applyHighlights=true&language=${currentLanguage}`;

  if (takeAmount > 0) {
    path += `&take=${takeAmount}`;
  }

  if (skipAmount > 0) {
    path += `&skip=${skipAmount}`;
  }

  if (isListingPage) {
    if (selectedSubjects.length) {
      path += `&categories=${selectedSubjects.join(',')}`;
    }
    path += `&eventStatus=${selectedEventStatus ?? "upcoming"}`;

    if (selectedSortOrder) {
      path += `&sortOrder=${selectedSortOrder}`;
    }
  } else {
    if (selectedContentTypes.length) {
      path += `&contentTypesOr=${selectedContentTypes.join(',')}`;
    }
    if (selectedSubjects.length) {
      path += `&categoriesOr=${selectedSubjects.join(',')}`;
    }
  }

  if (apiAncestors) {
    path += `&ancestors=${apiAncestors}`;
  }

  try {
    searchIsLoading = true;
    toggleLoadingSpinner(isLoadMore);

    const response = await fetch(path, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(response.statusText);
    }

    return result;
  } catch (error) {
    console.log(error);
  } finally {
    searchIsLoading = false;
    toggleLoadingSpinner(isLoadMore);
  }
};

export const getPossibleFiltersForQuery = async (query: string | null): Promise<IFilters | void> => {
  let res;
  try {
    const response = await fetch(`/api/search/filters?pageid=${pageId}&query=${query}&language=${currentLanguage}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
    });

    res = await response.json();

    if (!response.ok) {
      throw new Error(response.statusText);
    }

  } catch (error) {
    console.log(error);
  }

  if (res) {
    return res;
  }
  return {contentTypes: [], subjects: []};
};
